import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTabActivity } from '../contexts/TabActivityContext';
import {
  AlertCircle,
  CheckCircle,
  Download,
  Pause,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Upload,
  Workflow,
  X,
  Clock3,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAutomationWorkspaceContext } from '../contexts/AutomationWorkspaceContext';
import { useTabState } from '../contexts/TabStateContext';
import { commitOwnedSearch } from '../navigation/historyState';
import type { DebounceInterval, PromptRun } from '../services/api';
import IndexGallery from './IndexGallery';
import AutomationExecutionHistory from './AutomationExecutionHistory';
import {
  AUTOMATION_DEBOUNCE_OPTIONS,
  getAutomationRuntimeBadge,
  getAutomationRuntimeError,
  isAutomationDebounceInterval,
  normalizeExportDestinationSubpath,
} from '../utils/automationRuntime';
import type {
  AutomationMessage,
  AutomationRuntimeFields,
} from '../utils/automationRuntime';
import CustomSelect, { SelectOption } from './CustomSelect';
import './AutomationsTab.css';

export type AutomationView = 'import' | 'export';

const VIEW_OWNED_KEYS = ['automation'];
const DEBOUNCE_LABELS: Record<DebounceInterval, string> = {
  '5m': '5 minutes',
  '10m': '10 minutes',
  '1h': '1 hour',
  '1d': '1 day',
};
const DEBOUNCE_OPTIONS: SelectOption[] = AUTOMATION_DEBOUNCE_OPTIONS.map((value) => ({
  value,
  label: DEBOUNCE_LABELS[value],
}));
const INVALID_STORED_SELECT_VALUE = '__invalid_automation_stored_value__';
const INVALID_STORED_SELECT_OPTION: SelectOption = {
  value: INVALID_STORED_SELECT_VALUE,
  label: 'Invalid stored value',
};

const isAutomationView = (value: string | null): value is AutomationView => (
  value === 'import' || value === 'export'
);

const withUnavailableOption = (
  options: SelectOption[],
  selectedValue: string | null | undefined,
): SelectOption[] => {
  const normalizedValue = String(selectedValue || '').trim();
  if (!normalizedValue || options.some((option) => option.value === normalizedValue)) {
    return options;
  }
  return [
    ...options,
    { value: normalizedValue, label: `${normalizedValue} (unavailable)` },
  ];
};

const getSavedStatus = (
  savedDraft: (AutomationRuntimeFields & { enabled: boolean; paused: boolean }) | null,
  isLoading: boolean,
  error: string | null,
): { className: string; label: string } => (
  savedDraft
    ? getAutomationRuntimeBadge(savedDraft)
    : isLoading
      ? { className: 'loading', label: 'Loading' }
      : error
        ? { className: 'error', label: 'Unavailable' }
        : { className: 'unconfigured', label: 'Not configured' }
);

interface InlineMessageProps {
  message: AutomationMessage;
}

const InlineMessage: React.FC<InlineMessageProps> = ({ message }) => (
  <div
    className={`automations-notice automations-notice--${message.type}`}
    role={message.type === 'error' ? 'alert' : 'status'}
    aria-live="polite"
  >
    {message.type === 'success' ? (
      <CheckCircle size={16} aria-hidden="true" />
    ) : message.type === 'error' ? (
      <AlertCircle size={16} aria-hidden="true" />
    ) : (
      <RefreshCw size={16} aria-hidden="true" />
    )}
    <span>{message.text}</span>
  </div>
);

interface IndexAutomationPanelProps {
  onClose?: () => void;
  onSelectIndex?: (indexId: string) => void;
  runs?: PromptRun[];
}

const IndexAutomationPanel: React.FC<IndexAutomationPanelProps> = ({ onClose, onSelectIndex, runs }) => {
  const { isActive } = useTabActivity();
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const location = useLocation();
  const navigate = useNavigate();
  const { indexTabState, automationsTabState, setAutomationsTabState } = useTabState();
  const importTabRef = useRef<HTMLButtonElement>(null);
  const exportTabRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!isActive) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      // Let the shared select consume its first Escape before closing the drawer.
      if (event.key === 'Escape' && panelRef.current?.querySelector('.custom-select-menu.open')) return;
      if (event.key === 'Escape') onCloseRef.current?.();
      if (event.key !== 'Tab' || !panelRef.current) return;
      const controls = Array.from(panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]',
      )).filter((element) => element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden');
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape, true);
    return () => {
      document.removeEventListener('keydown', closeOnEscape, true);
      if (isActiveRef.current) previouslyFocused?.focus();
    };
  }, [isActive]);

  const {
    eligibleIndexes,
    selectedIndex,
    importConnectors,
    exportConnectors,
    prompts,
    importDraft,
    exportDraft,
    savedImportDraft,
    savedExportDraft,
    setImportDraft,
    setExportDraft,
    importDirty,
    exportDirty,
    importValidationError,
    exportValidationError,
    pendingImportMutation,
    pendingExportMutation,
    isLoadingIndexes,
    isLoadingConnectors,
    isLoadingEngines,
    isLoadingImport,
    isLoadingExport,
    hasLoadedImport,
    hasLoadedExport,
    isSavingImport,
    isSavingExport,
    indexesError,
    connectorsError,
    enginesError,
    importError,
    exportError,
    importMessage,
    exportMessage,
    refresh,
    retryImport,
    retryExport,
    saveImport,
    saveExport,
    toggleImportPause,
    toggleExportPause,
    resetImportDraft,
    resetExportDraft,
  } = useAutomationWorkspaceContext();
  const selectedIndexId = indexTabState.selectedIndexId;
  const requestedView = new URLSearchParams(location.search).get('automation');
  const activeView: AutomationView = isAutomationView(requestedView)
    ? requestedView : automationsTabState.activeView === 'export' ? 'export' : 'import';
  const openedIndexRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedIndexId || selectedIndex?.index_id !== selectedIndexId || openedIndexRef.current === selectedIndexId) return;
    openedIndexRef.current = selectedIndexId;
    // Baselines load with Index; opening later observes fresh scheduler state.
    if (hasLoadedImport && !isLoadingImport) void retryImport();
    if (hasLoadedExport && !isLoadingExport) void retryExport();
  }, [selectedIndexId, selectedIndex?.index_id, hasLoadedImport, hasLoadedExport, isLoadingImport, isLoadingExport, retryImport, retryExport]);

  const importConnectorOptions = useMemo<SelectOption[]>(() => withUnavailableOption([
    { value: '', label: 'Select a source connector…' },
    ...importConnectors.map((connector) => ({
      value: connector.connector_id,
      label: `${connector.name} (${connector.provider.toUpperCase()})`,
    })),
  ], importDraft.connector_id), [importConnectors, importDraft.connector_id]);

  const exportConnectorOptions = useMemo<SelectOption[]>(() => withUnavailableOption([
    { value: '', label: 'Select a destination connector…' },
    ...exportConnectors.map((connector) => ({
      value: connector.connector_id,
      label: `${connector.name} (${connector.provider.toUpperCase()})`,
    })),
  ], exportDraft.destination_connector_id), [exportConnectors, exportDraft.destination_connector_id]);

  const promptOptions = useMemo<SelectOption[]>(() => withUnavailableOption([
    { value: '', label: 'Select an extraction engine…' },
    ...prompts.map((prompt) => ({ value: prompt.prompt_id, label: prompt.name })),
  ], importDraft.prompt_id), [importDraft.prompt_id, prompts]);

  const debounceControlValue = importDraft.debounce_interval === null
    ? INVALID_STORED_SELECT_VALUE
    : importDraft.debounce_interval;

  const debounceOptions = useMemo<SelectOption[]>(() => withUnavailableOption(
    [
      ...DEBOUNCE_OPTIONS,
      ...(importDraft.debounce_interval === null ? [INVALID_STORED_SELECT_OPTION] : []),
    ],
    debounceControlValue,
  ), [debounceControlValue, importDraft.debounce_interval]);
  const importConnectorAvailable = importConnectors.some(
    (connector) => connector.connector_id === importDraft.connector_id,
  );
  const exportConnectorAvailable = exportConnectors.some(
    (connector) => connector.connector_id === exportDraft.destination_connector_id,
  );
  const exportSubpath = normalizeExportDestinationSubpath(exportDraft.destination_subpath);
  const promptAvailable = prompts.some((prompt) => prompt.prompt_id === importDraft.prompt_id);
  const importConnectorInvalid = Boolean(
    importDraft.connector_id
    && !importConnectorAvailable,
  );
  const importPromptInvalid = Boolean(
    importDraft.prompt_id
    && !promptAvailable,
  );
  const exportConnectorInvalid = Boolean(
    exportDraft.destination_connector_id
    && !exportConnectorAvailable,
  );
  const debounceIntervalValid = isAutomationDebounceInterval(importDraft.debounce_interval);
  const importStatus = !hasLoadedImport && activeView === 'export'
    ? { className: 'loading', label: 'Not loaded' }
    : getSavedStatus(savedImportDraft, isLoadingImport, importError);
  const exportStatus = getSavedStatus(savedExportDraft, isLoadingExport, exportError);
  const importRuntimeError = savedImportDraft
    ? getAutomationRuntimeError(
      savedImportDraft,
      'Scheduler provisioning failed. Import automation is not active.',
    )
    : null;
  const exportRuntimeError = savedExportDraft
    ? getAutomationRuntimeError(
      savedExportDraft,
      'Export automation setup failed. The automation is not active.',
    )
    : null;

  const goToIndex = useCallback(() => onClose?.(), [onClose]);
  const chooseView = useCallback((nextView: AutomationView, focus = false) => {
    setAutomationsTabState((current) => ({ ...current, activeView: nextView }));
    commitOwnedSearch({
      location, navigate, ownedKeys: VIEW_OWNED_KEYS, pathname: '/index',
      updates: { automation: nextView }, mode: 'replace',
    });
    if (focus) window.requestAnimationFrame(() => {
      (nextView === 'import' ? importTabRef : exportTabRef).current?.focus();
    });
  }, [location, navigate, setAutomationsTabState]);

  const handleViewKeyDown = useCallback((event: React.KeyboardEvent, view: AutomationView) => {
    let nextView: AutomationView | null = null;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'Home') {
      nextView = 'import';
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'End') {
      nextView = 'export';
    }
    if (!nextView) return;
    event.preventDefault();
    if (nextView === view) return;
    chooseView(nextView, true);
  }, [chooseView]);

  const activeReferencesError = [
    indexesError,
    connectorsError,
    ...(activeView === 'import' ? [enginesError] : []),
  ].filter((message): message is string => Boolean(message)).join(' ') || null;
  const isRefreshing = (
    isLoadingIndexes
    || isLoadingConnectors
    || (activeView === 'import' && isLoadingEngines)
    || isLoadingImport
    || isLoadingExport
  );
  const isMutating = isSavingImport || isSavingExport;
  const importFieldsDisabled = (
    !selectedIndex
    || isLoadingImport
    || isSavingImport
    || Boolean(pendingImportMutation)
  );
  const exportFieldsDisabled = (
    !selectedIndex
    || isLoadingExport
    || isSavingExport
    || Boolean(pendingExportMutation)
  );
  const importIsPaused = savedImportDraft?.paused === true;
  const exportIsPaused = savedExportDraft?.paused === true;
  const importResumeDisabled = Boolean(
    importIsPaused
    && importValidationError,
  );
  const exportResumeDisabled = Boolean(
    exportIsPaused
    && exportValidationError,
  );
  const importPauseDisabled = (
    !selectedIndex
    || !savedImportDraft
    || !savedImportDraft.enabled
    || savedImportDraft.runtime_status === 'provisioning'
    || importDirty
    || isLoadingImport
    || isSavingImport
    || pendingImportMutation === 'save'
    || importResumeDisabled
  );
  const exportPauseDisabled = (
    !selectedIndex
    || !savedExportDraft
    || !savedExportDraft.enabled
    || savedExportDraft.runtime_status === 'provisioning'
    || exportDirty
    || isLoadingExport
    || isSavingExport
    || pendingExportMutation === 'save'
    || exportResumeDisabled
  );
  const showIndexLoadingState = isLoadingIndexes && eligibleIndexes.length === 0;
  const showIndexErrorOnly = Boolean(indexesError) && eligibleIndexes.length === 0;
  const hideImportEditor = !savedImportDraft && (isLoadingImport || Boolean(importError));
  const hideExportEditor = !savedExportDraft && (isLoadingExport || Boolean(exportError));

  const automationEnabled = Boolean(savedImportDraft?.enabled || savedExportDraft?.enabled);
  const importActive = importStatus.className === 'active';
  const exportActive = exportStatus.className === 'active';
  const nextExecution = savedImportDraft?.next_execution_at;
  const [scheduleNow, setScheduleNow] = useState(Date.now);
  useEffect(() => {
    const currentTime = Date.now();
    setScheduleNow(currentTime);
    const deadline = nextExecution ? Date.parse(nextExecution) : NaN;
    if (!Number.isFinite(deadline) || deadline <= currentTime) return;
    // One display-only deadline, not a polling loop or a guessed next run.
    const timer = window.setTimeout(() => setScheduleNow(Date.now()), deadline - currentTime + 50);
    return () => window.clearTimeout(timer);
  }, [nextExecution]);
  const savedEngine = prompts.find((prompt) => prompt.prompt_id === savedImportDraft?.prompt_id);

  return (
    <div className="index-automation-overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isMutating) onClose?.();
    }}>
    <div
      className="index-automation-drawer automations-tab"
      role="dialog"
      aria-modal="true"
      aria-labelledby="automations-title"
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="automations-workspace">
        <header className="automations-header">
          <div className="automations-header-copy">
            <Workflow size={20} aria-hidden="true" />
            <div>
              <h1 id="automations-title">Index automation</h1>
              <p className="automations-selected-index">
                <span>Selected index</span>
                <strong>{selectedIndex?.name || 'Choose an index'}</strong>
              </p>
            </div>
          </div>
          <div className="automations-header-controls">
            <button type="button" className="automations-button automations-button--secondary"
              onClick={() => navigate('/connectors')} disabled={isMutating}>
              <Plus size={16} aria-hidden="true" />
              Add connector
            </button>
            <button type="button" className="automations-icon-button" onClick={() => void refresh()}
              disabled={isRefreshing || isMutating} title="Refresh automation settings" aria-label="Refresh automation settings">
              <RefreshCw size={16} className={isRefreshing ? 'automations-spinning' : undefined} />
            </button>
            <button type="button" className="automations-icon-button" onClick={onClose}
              disabled={isMutating} title="Close automation" aria-label="Close automation">
              <X size={18} />
            </button>
          </div>
        </header>
        {onSelectIndex && (
          <details className="automation-index-picker">
            <summary>Change index</summary>
            <IndexGallery indexes={eligibleIndexes} selectedIndexId={selectedIndexId || ''}
              onSelect={onSelectIndex} disabled={isMutating || Boolean(pendingImportMutation || pendingExportMutation)}
              isLoading={isLoadingIndexes} />
          </details>
        )}
        {automationEnabled && (
          <section className="automation-next-execution" aria-label="Next automation execution">
            <div className="automation-section-heading"><Clock3 size={15} /><strong>Up next</strong></div>
            {savedImportDraft?.enabled && (
              <div className="automation-next-action">
                <strong>Import{savedImportDraft.prompt_id ? ' → Process' : ''}{savedImportDraft.prompt_id && exportActive ? ' → Export' : ''}</strong>
                <span>{importActive
                  ? nextExecution
                    ? Date.parse(nextExecution) > scheduleNow
                      ? new Date(nextExecution).toLocaleString()
                      : 'Scheduled check time reached · awaiting import activity'
                    : savedImportDraft.next_execution_error || 'Next check time is not available yet. Refresh to check.'
                  : importStatus.label}</span>
                {savedImportDraft.prompt_id && <small>New media is processed with {savedEngine?.name || 'the saved engine'} after import completes.</small>}
              </div>
            )}
            {savedExportDraft?.enabled && (
              <div className="automation-next-action">
                <strong>Export completed results</strong>
                <span>{exportActive ? 'After the next workflow execution completes' : exportStatus.label}</span>
                <small>Event-triggered · no fixed execution time</small>
              </div>
            )}
          </section>
        )}

        {activeReferencesError && (
          <div className="automations-notice automations-notice--error" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            <span>{activeReferencesError}</span>
            <button type="button" onClick={() => void refresh()}>Try again</button>
          </div>
        )}

        {showIndexLoadingState ? (
          <div className="automations-loading automations-loading--workspace" role="status">
            <RefreshCw size={18} className="automations-spinning" aria-hidden="true" />
            <span>Loading indexes…</span>
          </div>
        ) : showIndexErrorOnly ? null : !isLoadingIndexes && eligibleIndexes.length === 0 ? (
          <section className="automations-empty" aria-labelledby="automations-empty-title">
            <Workflow size={30} aria-hidden="true" />
            <h2 id="automations-empty-title">No index is available for automation</h2>
            <p>Create a standard index before configuring an import or export automation.</p>
            <button type="button" onClick={goToIndex}>Go to Index</button>
          </section>
        ) : (
          <>
            <div className="automations-view-switcher" role="tablist" aria-label="Automation type">
              <button
                ref={importTabRef}
                id="automations-import-tab"
                type="button"
                role="tab"
                aria-selected={activeView === 'import'}
                aria-controls="automations-import-panel"
                tabIndex={activeView === 'import' ? 0 : -1}
                className={activeView === 'import' ? 'is-active' : ''}
                onClick={() => chooseView('import')}
                onKeyDown={(event) => handleViewKeyDown(event, 'import')}
                disabled={isMutating}
              >
                <Upload size={16} aria-hidden="true" />
                <span>Import</span>
                <span className={`automations-status automations-status--${importStatus.className}`}>
                  {importStatus.label}
                </span>
              </button>
              <button
                ref={exportTabRef}
                id="automations-export-tab"
                type="button"
                role="tab"
                aria-selected={activeView === 'export'}
                aria-controls="automations-export-panel"
                tabIndex={activeView === 'export' ? 0 : -1}
                className={activeView === 'export' ? 'is-active' : ''}
                onClick={() => chooseView('export')}
                onKeyDown={(event) => handleViewKeyDown(event, 'export')}
                disabled={isMutating}
              >
                <Download size={16} aria-hidden="true" />
                <span>Export</span>
                <span className={`automations-status automations-status--${exportStatus.className}`}>
                  {exportStatus.label}
                </span>
              </button>
            </div>

            <section
                id="automations-import-panel"
                className="automations-panel"
                role="tabpanel"
                aria-labelledby="automations-import-tab"
                aria-busy={isLoadingImport}
                hidden={activeView !== 'import'}
              >
                <div className="automations-panel-intro">
                  <p>Watch connected storage for new media and process each match automatically.</p>
                  <label className="automations-enable-toggle">
                    <span>
                      <strong>Enabled</strong>
                      <small>Run this automation on its schedule</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={importDraft.enabled}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setImportDraft((current) => ({ ...current, enabled, paused: false }));
                      }}
                      disabled={
                        !selectedIndex
                        || isLoadingImport
                        || isSavingImport
                        || Boolean(pendingImportMutation)
                      }
                      aria-label="Enable import automation"
                    />
                    <span className="automations-switch" aria-hidden="true" />
                  </label>
                </div>

                {isLoadingImport && !savedImportDraft && (
                  <div className="automations-loading" role="status">
                    <RefreshCw size={18} className="automations-spinning" aria-hidden="true" />
                    <span>Loading import settings…</span>
                  </div>
                )}

                {importError && (
                  <div className="automations-notice automations-notice--error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <span>{importError}</span>
                    <button type="button" onClick={() => void retryImport()}>Try again</button>
                  </div>
                )}
                {importRuntimeError && (
                  <div className="automations-notice automations-notice--error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <span>{importRuntimeError}</span>
                  </div>
                )}
                {importMessage && !importRuntimeError && <InlineMessage message={importMessage} />}

                <div
                  className={`automations-form ${importFieldsDisabled ? 'is-disabled' : ''}`}
                  hidden={hideImportEditor}
                >
                  <div className="automations-fields automations-fields--two-column">
                    <div className="automations-field">
                      <span className="automations-field-label">Source connector</span>
                      <CustomSelect
                        value={importDraft.connector_id}
                        onChange={(value) => setImportDraft((current) => ({ ...current, connector_id: value }))}
                        options={importConnectorOptions}
                        placeholder="Select a source connector…"
                        ariaLabel="Source connector"
                        ariaDescribedBy={importConnectorInvalid ? 'automations-import-connector-error' : undefined}
                        ariaInvalid={importConnectorInvalid}
                        disabled={importFieldsDisabled || isLoadingConnectors}
                        className="automations-select"
                      />
                      {importConnectorInvalid && (
                        <span
                          id="automations-import-connector-error"
                          className="automations-field-error"
                          role="alert"
                        >
                          Choose an available import connector.
                        </span>
                      )}
                    </div>
                    <div className="automations-field">
                      <span className="automations-field-label">Extraction engine</span>
                      <CustomSelect
                        value={importDraft.prompt_id}
                        onChange={(value) => setImportDraft((current) => ({
                          ...current,
                          prompt_id: value,
                        }))}
                        options={promptOptions}
                        placeholder="Select an extraction engine…"
                        ariaLabel="Extraction engine"
                        ariaDescribedBy={importPromptInvalid ? 'automations-import-prompt-error' : undefined}
                        ariaInvalid={importPromptInvalid}
                        disabled={importFieldsDisabled || isLoadingEngines}
                        className="automations-select"
                      />
                      {importPromptInvalid && (
                        <span
                          id="automations-import-prompt-error"
                          className="automations-field-error"
                          role="alert"
                        >
                          Choose an available extraction engine.
                        </span>
                      )}
                    </div>
                    <label className="automations-field" htmlFor="automations-source-prefix">
                      <span className="automations-field-label">Folder path</span>
                      <input
                        id="automations-source-prefix"
                        type="text"
                        value={importDraft.source_prefix}
                        onChange={(event) => {
                          const sourcePrefix = event.target.value;
                          setImportDraft((current) => ({
                            ...current,
                            source_prefix: sourcePrefix,
                          }));
                        }}
                        placeholder="e.g. uploads/"
                        disabled={importFieldsDisabled}
                      />
                    </label>
                    <label className="automations-field" htmlFor="automations-file-pattern">
                      <span className="automations-field-label">File pattern</span>
                      <input
                        id="automations-file-pattern"
                        type="text"
                        value={importDraft.file_pattern}
                        onChange={(event) => {
                          const filePattern = event.target.value;
                          setImportDraft((current) => ({
                            ...current,
                            file_pattern: filePattern,
                          }));
                        }}
                        placeholder="*"
                        disabled={importFieldsDisabled}
                      />
                    </label>
                    <div className="automations-field">
                      <span className="automations-field-label">Check interval</span>
                      <CustomSelect
                        value={debounceControlValue}
                        onChange={(value) => setImportDraft((current) => ({
                          ...current,
                          debounce_interval: value as DebounceInterval,
                        }))}
                        options={debounceOptions}
                        ariaLabel="Check interval"
                        ariaDescribedBy={!debounceIntervalValid ? 'automations-import-interval-error' : undefined}
                        ariaInvalid={!debounceIntervalValid}
                        disabled={importFieldsDisabled}
                        className="automations-select"
                      />
                      {!debounceIntervalValid && (
                        <span
                          id="automations-import-interval-error"
                          className="automations-field-error"
                          role="alert"
                        >
                          Choose an available check interval.
                        </span>
                      )}
                    </div>
                    <label className="automations-check-row">
                      <input
                        type="checkbox"
                        checked={importDraft.recursive}
                        onChange={(event) => {
                          const recursive = event.target.checked;
                          setImportDraft((current) => ({ ...current, recursive }));
                        }}
                        disabled={importFieldsDisabled}
                      />
                      <span>
                        <strong>Include subfolders</strong>
                        <small>Match files below the selected folder path</small>
                      </span>
                    </label>
                  </div>

                  {importDraft.prompt_id && promptAvailable && (
                    <div className="automations-notice automations-notice--info" role="note">
                      <span>
                        New imports use this engine's saved processing settings, extracted fields,
                        and filters.
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/workflows?index=${encodeURIComponent(selectedIndexId || '')}&prompt=${encodeURIComponent(importDraft.prompt_id)}&edit=1`)}
                      >
                        Edit engine
                      </button>
                    </div>
                  )}
                </div>

                <div className="automations-actions" hidden={hideImportEditor}>
                  <button
                    type="button"
                    className="automations-button automations-button--quiet"
                    onClick={resetImportDraft}
                    disabled={!importDirty || isSavingImport || Boolean(pendingImportMutation)}
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    Discard changes
                  </button>
                  <div className="automations-actions-primary">
                    <button
                      type="button"
                      className="automations-button automations-button--secondary"
                      onClick={() => void toggleImportPause().then((saved) => { if (saved) return retryImport(); })}
                      disabled={importPauseDisabled}
                      title={
                        savedImportDraft?.runtime_status === 'provisioning'
                          ? 'Wait for the current scheduler update to finish'
                          : importDirty
                            ? 'Save or discard changes before pausing'
                            : undefined
                      }
                    >
                      {importIsPaused ? <Play size={15} aria-hidden="true" /> : <Pause size={15} aria-hidden="true" />}
                      {importIsPaused ? 'Resume' : 'Pause'}
                    </button>
                    <button
                      type="button"
                      className="automations-button automations-button--primary"
                      onClick={() => void saveImport().then((saved) => { if (saved) return retryImport(); })}
                      disabled={
                        !selectedIndex
                        || !importDirty
                        || Boolean(importValidationError)
                        || savedImportDraft?.runtime_status === 'provisioning'
                        || pendingImportMutation === 'status'
                        || Boolean(importError)
                        || isLoadingImport
                        || isSavingImport
                      }
                    >
                      {isSavingImport ? (
                        <RefreshCw size={15} className="automations-spinning" aria-hidden="true" />
                      ) : (
                        <CheckCircle size={15} aria-hidden="true" />
                      )}
                      {isSavingImport ? 'Saving…' : importDraft.enabled !== Boolean(savedImportDraft?.enabled) ? (importDraft.enabled ? 'Enable import automation' : 'Disable import automation') : 'Save changes'}
                    </button>
                  </div>
                </div>
            </section>

            <section
                id="automations-export-panel"
                className="automations-panel"
                role="tabpanel"
                aria-labelledby="automations-export-tab"
                aria-busy={isLoadingExport}
                hidden={activeView !== 'export'}
              >
                <div className="automations-panel-intro">
                  <p>Deliver structured results to connected storage after processing completes.</p>
                  <label className="automations-enable-toggle">
                    <span>
                      <strong>Enabled</strong>
                      <small>Deliver new completed results automatically</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={exportDraft.enabled}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setExportDraft((current) => ({ ...current, enabled, paused: false }));
                      }}
                      disabled={
                        !selectedIndex
                        || isLoadingExport
                        || isSavingExport
                        || Boolean(pendingExportMutation)
                      }
                      aria-label="Enable export automation"
                    />
                    <span className="automations-switch" aria-hidden="true" />
                  </label>
                </div>

                {isLoadingExport && !savedExportDraft && (
                  <div className="automations-loading" role="status">
                    <RefreshCw size={18} className="automations-spinning" aria-hidden="true" />
                    <span>Loading export settings…</span>
                  </div>
                )}
                {exportError && (
                  <div className="automations-notice automations-notice--error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <span>{exportError}</span>
                    <button type="button" onClick={() => void retryExport()}>Try again</button>
                  </div>
                )}
                {exportRuntimeError && (
                  <div className="automations-notice automations-notice--error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <span>{exportRuntimeError}</span>
                  </div>
                )}
                {exportMessage && !exportRuntimeError && <InlineMessage message={exportMessage} />}

                <div
                  className={`automations-form ${exportFieldsDisabled ? 'is-disabled' : ''}`}
                  hidden={hideExportEditor}
                >
                  <div className="automations-fields automations-fields--two-column">
                    <div className="automations-field">
                      <span className="automations-field-label">Destination connector</span>
                      <CustomSelect
                        value={exportDraft.destination_connector_id}
                        onChange={(value) => setExportDraft((current) => ({
                          ...current,
                          destination_connector_id: value,
                        }))}
                        options={exportConnectorOptions}
                        placeholder="Select a destination connector…"
                        ariaLabel="Destination connector"
                        ariaDescribedBy={exportConnectorInvalid ? 'automations-export-connector-error' : undefined}
                        ariaInvalid={exportConnectorInvalid}
                        disabled={exportFieldsDisabled || isLoadingConnectors}
                        className="automations-select"
                      />
                      {exportConnectorInvalid && (
                        <span
                          id="automations-export-connector-error"
                          className="automations-field-error"
                          role="alert"
                        >
                          Choose an available export connector.
                        </span>
                      )}
                    </div>
                    <label className="automations-field" htmlFor="automations-destination-subpath">
                      <span className="automations-field-label">Export folder</span>
                      <input
                        id="automations-destination-subpath"
                        type="text"
                        value={exportDraft.destination_subpath}
                        onChange={(event) => {
                          const destinationSubpath = event.target.value;
                          setExportDraft((current) => ({
                            ...current,
                            destination_subpath: destinationSubpath,
                          }));
                        }}
                        placeholder="Optional, e.g. exports/team-a"
                        disabled={exportFieldsDisabled}
                        aria-describedby={exportSubpath.error ? 'automations-export-subpath-error' : undefined}
                        aria-invalid={Boolean(exportSubpath.error) || undefined}
                      />
                      {exportSubpath.error && (
                        <span
                          id="automations-export-subpath-error"
                          className="automations-field-error"
                          role="alert"
                        >
                          {exportSubpath.error}
                        </span>
                      )}
                    </label>
                  </div>
                </div>

                <div className="automations-actions" hidden={hideExportEditor}>
                  <button
                    type="button"
                    className="automations-button automations-button--quiet"
                    onClick={resetExportDraft}
                    disabled={!exportDirty || isSavingExport || Boolean(pendingExportMutation)}
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    Discard changes
                  </button>
                  <div className="automations-actions-primary">
                    <button
                      type="button"
                      className="automations-button automations-button--secondary"
                      onClick={() => void toggleExportPause()}
                      disabled={exportPauseDisabled}
                      title={
                        savedExportDraft?.runtime_status === 'provisioning'
                          ? 'Wait for the current scheduler update to finish'
                          : exportDirty
                            ? 'Save or discard changes before pausing'
                            : undefined
                      }
                    >
                      {exportIsPaused ? <Play size={15} aria-hidden="true" /> : <Pause size={15} aria-hidden="true" />}
                      {exportIsPaused ? 'Resume' : 'Pause'}
                    </button>
                    <button
                      type="button"
                      className="automations-button automations-button--primary"
                      onClick={() => void saveExport()}
                      disabled={
                        !selectedIndex
                        || !exportDirty
                        || Boolean(exportValidationError)
                        || savedExportDraft?.runtime_status === 'provisioning'
                        || pendingExportMutation === 'status'
                        || Boolean(exportError)
                        || isLoadingExport
                        || isSavingExport
                      }
                    >
                      {isSavingExport ? (
                        <RefreshCw size={15} className="automations-spinning" aria-hidden="true" />
                      ) : (
                        <CheckCircle size={15} aria-hidden="true" />
                      )}
                      {isSavingExport ? 'Saving…' : exportDraft.enabled !== Boolean(savedExportDraft?.enabled) ? (exportDraft.enabled ? 'Enable export automation' : 'Disable export automation') : 'Save changes'}
                    </button>
                  </div>
                </div>
            </section>
          </>
        )}
        {automationEnabled && selectedIndexId && (
          <AutomationExecutionHistory key={selectedIndexId} indexId={selectedIndexId} runs={runs} onImportActivity={retryImport} />
        )}
      </div>
    </div>
    </div>
  );
};

export default IndexAutomationPanel;
